import { z } from 'zod';
import { cursorPageQuerySchema } from './pagination.js';
// `Exercise.contraindications` is the same `InjuryArea` enum a user's
// `injuryFlags` uses — the two are intersected to exclude an exercise from
// planning, so they must be one vocabulary, not two that agree by hand.
import { INJURY_AREAS } from './user-detail.js';
/**
 * The exercise library.
 *
 * Reference data shared by every user, like the food catalog, and governed by
 * the same rules: `support` reads and changes nothing, mutations audit inside
 * their own transaction, and nothing is deleted — an exercise that appears in
 * someone's training history must never disappear under it (`SetLog.exercise`
 * is Restrict on delete, so the database agrees).
 */
export const EXERCISE_CATEGORIES = ['push', 'pull', 'legs', 'core', 'cardio', 'mobility', 'yoga'];
export const MUSCLE_GROUPS = [
    'chest',
    'back',
    'shoulders',
    'biceps',
    'triceps',
    'forearms',
    'abs',
    'obliques',
    'quads',
    'hamstrings',
    'glutes',
    'calves',
    'lats',
    'traps',
    'lower_back',
    'rear_delts',
    'full_body',
    'cardio',
];
/**
 * How much competence a movement demands. A real planning constraint: a user
 * with beginner experience is never offered an `advanced` movement.
 */
export const EXERCISE_DIFFICULTIES = ['beginner', 'intermediate', 'advanced'];
/**
 * What a movement needs, as a requirements list: every value has to be
 * available where the user trains for the movement to be offered.
 *
 * The last five are gear no home, park or mat guarantees, offered at a gym
 * only. They are appended rather than slotted in beside their neighbours
 * because stored equipment lists and catalog entries are written in this
 * order, and reordering the vocabulary would make every existing one out of order.
 */
export const EQUIPMENT = [
    'none',
    'dumbbell',
    'barbell',
    'machine',
    'cable',
    'kettlebell',
    'resistance_band',
    'bodyweight',
    'cardio_machine',
    'bench',
    'pull_up_bar',
    'dip_station',
    'box',
    'stability_ball',
];
/**
 * How a movement behaves, as opposed to what it trains.
 *
 * Every one of the five below is **nullable on the row, and null means "nobody
 * has said", never "no"**. The importer fills in what free-exercise-db states
 * (`mechanic`, and `impact` for the categories that answer it) and what a
 * documented name rule can decide; the rest is a curator's call at review,
 * alongside `contraindications`. Rendering a blank as a value would defeat the
 * point — an operator working the review queue has to be able to see which
 * fields nobody has answered.
 */
export const MECHANICS = ['compound', 'isolation'];
/**
 * What a program balances its coverage over — deliberately not a
 * compound-to-isolation quota. Vertical and horizontal are separate members
 * because a week of nothing but rows is not a week that pulls.
 */
export const MOVEMENT_PATTERNS = [
    'squat',
    'hinge',
    'lunge',
    'horizontal_push',
    'vertical_push',
    'horizontal_pull',
    'vertical_pull',
    'carry',
    'rotation',
];
/**
 * One limb at a time, or both. Changes the progression step rather than being
 * a label: unilateral load is prescribed per limb, so the same plate on each
 * side is twice the systemic jump.
 */
export const LATERALITIES = ['bilateral', 'unilateral'];
/** What the movement asks of joints on landing. `high` is jumping work. */
export const IMPACTS = ['low', 'medium', 'high'];
/**
 * The position the working part of a movement is done in: what prenatal
 * exercise guidance is written in terms of. `quadruped` covers hands and
 * knees and every plank or push-up position with the belly off the floor;
 * `prone` is lying face down with the belly on the floor or a pad;
 * `inverted` is head below hips with weight on the head, shoulders or hands;
 * `hanging` is hanging from a bar or rings.
 *
 * Nullable on the row like the five above, and null is "not yet assessed".
 * Never sent to the app: these three fields exist for plan eligibility and
 * the review screen, and reach no public response.
 */
export const BODY_POSITIONS = [
    'standing',
    'seated',
    'kneeling',
    'quadruped',
    'side_lying',
    'supine',
    'prone',
    'inverted',
    'hanging',
];
/**
 * How a curated alternative relates to the exercise it targets — see
 * `ExerciseEdge` in schema.prisma. Added and removed in the console, or
 * pushed with `catalog:push`.
 */
export const EXERCISE_RELATIONS = [
    'progression',
    'regression',
    'lower_skill',
    'lower_impact',
    'equipment_alternative',
    'time_saving',
];
/**
 * The demo clip.
 *
 * `key` is the object-storage key the row actually stores; `url` is resolved at
 * read time and is null when the bucket has no public endpoint configured —
 * which is the normal state in local development. Both are present because an
 * operator needs to know a clip *exists* even when this environment cannot play
 * it, and "no url" would otherwise be indistinguishable from "no clip".
 */
export const exerciseMediaSchema = z.object({
    key: z.string(),
    url: z.string().nullable(),
});
export const adminExerciseListItemSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    category: z.enum(EXERCISE_CATEGORIES),
    difficulty: z.enum(EXERCISE_DIFFICULTIES),
    primaryMuscles: z.array(z.enum(MUSCLE_GROUPS)),
    secondaryMuscles: z.array(z.enum(MUSCLE_GROUPS)),
    equipment: z.array(z.enum(EQUIPMENT)),
    /** Joints this movement loads enough to be unsafe on. */
    contraindications: z.array(z.enum(INJURY_AREAS)),
    /** Whether load is meaningful — bodyweight and cardio work have no 1RM. */
    isWeighted: z.boolean(),
    /**
     * How the movement behaves. All nullable, and null is "unassessed" — see the
     * vocabularies above. `mechanic` and `laterality` set the progression step;
     * `axialLoad` and `impact` are what an injury or pregnancy restriction reads.
     */
    mechanic: z.enum(MECHANICS).nullable(),
    movementPattern: z.enum(MOVEMENT_PATTERNS).nullable(),
    laterality: z.enum(LATERALITIES).nullable(),
    impact: z.enum(IMPACTS).nullable(),
    axialLoad: z.boolean().nullable(),
    /**
     * How the movement positions the body, which is what a pregnancy
     * restriction reads alongside `impact` and `difficulty`. Nullable for the
     * same reason: null is "not yet assessed", and the planner reads it
     * conservatively. `avoidInPregnancy` carries the prenatal guidance the other
     * two cannot state (deep twists, backbends, inversions, arm balances, belly
     * compression, Olympic lifts, a high fall risk).
     */
    bodyPosition: z.enum(BODY_POSITIONS).nullable(),
    trunkFlexion: z.boolean().nullable(),
    avoidInPregnancy: z.boolean().nullable(),
    /**
     * The movement needs a second person to hold, resist or hand something
     * over. Plans are trained alone, so the planner never schedules or suggests
     * a `true`; the row stays in the catalogue. Null is "not assessed" and,
     * unlike the three above, reads as solo-capable: an optional helper ("or
     * have a partner hold your feet") is not a partner movement. Admin-only,
     * like them.
     */
    requiresPartner: z.boolean().nullable(),
    isActive: z.boolean(),
    media: exerciseMediaSchema.nullable(),
    /**
     * Demonstration stills, carrying key and url for the same reason `media`
     * does: on a review screen, "this row has no images" and "this row has
     * images this environment cannot serve" are different problems, and an
     * operator checking whether the import worked has to be able to tell them
     * apart. Empty for hand-curated rows, which have no stills.
     */
    images: z.array(exerciseMediaSchema),
    /**
     * Where the row came from, e.g. "free-exercise-db:Barbell_Squat" for an
     * import or "curation:warrior-ii" for a row the catalog's exercises domain
     * authored by hand. Null only for a row created in this console.
     *
     * This is what makes the review queue possible: an imported, still-inactive
     * row is one nobody has looked at yet. Note that the `isImported` filter
     * reads "has a sourceId at all", so a curated row answers to it too, which
     * is the right side to land on: what the filter separates is rows a script
     * wrote from rows a person typed here.
     */
    sourceId: z.string().nullable(),
    sortOrder: z.number().int(),
    createdAt: z.string(),
});
export const EXERCISE_ACTIVITY_FILTERS = ['active', 'inactive'];
export const EXERCISE_MEDIA_FILTERS = ['with', 'without'];
/**
 * Hand-curated rows versus imported ones.
 *
 * `imported` rather than the dataset's name: a second dataset would otherwise
 * need a second filter value and every screen that reads this would have to
 * learn about it. What an operator is actually asking is "did a human write
 * this row, or did a script".
 */
export const EXERCISE_SOURCE_FILTERS = ['curated', 'imported'];
export const adminExerciseListQuerySchema = cursorPageQuerySchema.extend({
    search: z.string().trim().max(120).optional(),
    category: z.enum(EXERCISE_CATEGORIES).optional(),
    difficulty: z.enum(EXERCISE_DIFFICULTIES).optional(),
    equipment: z.enum(EQUIPMENT).optional(),
    muscle: z.enum(MUSCLE_GROUPS).optional(),
    activity: z.enum(EXERCISE_ACTIVITY_FILTERS).optional(),
    /** `imported` + `inactive` is the review queue: rows nobody has checked yet. */
    source: z.enum(EXERCISE_SOURCE_FILTERS).optional(),
    /** "Which exercises still have no demo clip" is the question this screen exists to answer. */
    media: z.enum(EXERCISE_MEDIA_FILTERS).optional(),
});
/**
 * How much training history points at this row — the reason `SetLog.exercise`
 * is Restrict on delete and this screen offers no delete either.
 */
export const exerciseUsageSchema = z.object({
    setLogs: z.number().int(),
    plannedItems: z.number().int(),
});
/**
 * One curated `ExerciseEdge`, oriented from this exercise's point of view.
 *
 * `direction: 'from'` means this exercise IS `relation` to `exercise` (e.g.
 * this exercise has a `progression` named `exercise`); `direction: 'to'`
 * means `exercise` IS `relation` to this one (e.g. `exercise` is this row's
 * `regression`). Both directions are shown on the review screen because a
 * curator checking one exercise's edges needs to see everything the table
 * says about it, not only the half where it is the `from` side.
 */
export const adminExerciseEdgeSchema = z.object({
    id: z.string().uuid(),
    direction: z.enum(['from', 'to']),
    relation: z.enum(EXERCISE_RELATIONS),
    exercise: z.object({
        id: z.string().uuid(),
        name: z.string(),
        slug: z.string(),
    }),
    /** How closely the two exercises train the same thing, 0-1. */
    stimulusMatch: z.number(),
    /** Positive: the *other* exercise is harder. Negative: easier. */
    difficultyDelta: z.number().int(),
    context: z.string().nullable(),
    reviewer: z.string(),
    evidence: z.string().nullable(),
});
/**
 * A curated alternative, added from the console: `toExerciseId` is the
 * alternative, and `relation` says what it is to the exercise in the URL (its
 * `progression`, its `regression`, ...). The reviewer recorded is the
 * operator's name.
 */
export const adminExerciseEdgeRequestSchema = z.object({
    toExerciseId: z.string().uuid(),
    relation: z.enum(EXERCISE_RELATIONS),
    /** 0-1: how closely the two train the same thing. */
    stimulusMatch: z.number().min(0).max(1),
    /** Positive: the alternative is harder. Small on purpose: a nudge, not a score. */
    difficultyDelta: z.number().int().min(-5).max(5),
    context: z.string().trim().min(1).max(280).nullable(),
    evidence: z.string().trim().min(1).max(500).nullable(),
});
/** Removing one alternative: the edge must start or end at the exercise in the URL. */
export const adminExerciseEdgeParamsSchema = z.object({
    id: z.string().uuid(),
    edgeId: z.string().uuid(),
});
export const adminExerciseDetailSchema = adminExerciseListItemSchema.extend({
    /** Ordered coaching cues shown on the app's "How to do it" card. */
    howToSteps: z.array(z.string()),
    updatedAt: z.string(),
    usage: exerciseUsageSchema,
    /** Curated alternatives in and out of this exercise. Empty means "not reviewed yet", never "no alternatives". */
    edges: z.array(adminExerciseEdgeSchema),
});
// --- Mutations --------------------------------------------------------------
export const adminExerciseParamsSchema = z.object({
    id: z.string().uuid(),
});
/**
 * Creating an exercise.
 *
 * `slug` is derived from the name rather than accepted: it is a stable key
 * other rows reference, and letting an operator type one invites two
 * near-identical slugs for the same movement.
 *
 * `primaryMuscles` must be non-empty — an exercise that trains nothing cannot
 * be selected by plan generation, so a row without it is inert rather than
 * merely incomplete.
 */
export const adminExerciseCreateRequestSchema = z.object({
    name: z.string().trim().min(2).max(120),
    category: z.enum(EXERCISE_CATEGORIES),
    /**
     * Defaults to intermediate, matching the column: an unstated difficulty that
     * read as `beginner` would offer the movement to the users least able to
     * judge whether they should attempt it.
     */
    difficulty: z.enum(EXERCISE_DIFFICULTIES).default('intermediate'),
    primaryMuscles: z.array(z.enum(MUSCLE_GROUPS)).min(1),
    secondaryMuscles: z.array(z.enum(MUSCLE_GROUPS)).default([]),
    equipment: z.array(z.enum(EQUIPMENT)).min(1),
    contraindications: z.array(z.enum(INJURY_AREAS)).default([]),
    howToSteps: z.array(z.string().trim().min(1).max(280)).default([]),
    isWeighted: z.boolean().default(true),
    /**
     * How the movement behaves. Nullable and defaulted to null rather than to a
     * value: an operator who has not answered has not answered, and a `bilateral`
     * that nobody chose is indistinguishable from one they did.
     */
    mechanic: z.enum(MECHANICS).nullable().default(null),
    movementPattern: z.enum(MOVEMENT_PATTERNS).nullable().default(null),
    laterality: z.enum(LATERALITIES).nullable().default(null),
    impact: z.enum(IMPACTS).nullable().default(null),
    axialLoad: z.boolean().nullable().default(null),
    bodyPosition: z.enum(BODY_POSITIONS).nullable().default(null),
    trunkFlexion: z.boolean().nullable().default(null),
    avoidInPregnancy: z.boolean().nullable().default(null),
    requiresPartner: z.boolean().nullable().default(null),
    /** The app's display order. Not unique, and not what the list pages on. */
    sortOrder: z.number().int().min(0).max(10_000).default(0),
});
/**
 * Editing an exercise.
 *
 * Every field optional and at least one required: the console sends what
 * changed, so the audit row can name it rather than recording a wholesale
 * overwrite of fields nobody touched.
 *
 * **`slug` is not here, and that is the point.** It is derived once at creation
 * and then frozen. Plan generation is constrained to `availableExerciseSlugs`
 * and stored plans reference the value they were generated against, so a slug
 * that follows a rename would silently stop matching rows that already exist.
 * Renaming changes what an operator reads; it does not change the key.
 *
 * `isActive` is not here either — deactivating requires a reason and is its own
 * audited endpoint, not a checkbox somebody can flip while editing cues.
 */
export const adminExerciseUpdateRequestSchema = z
    .object({
    name: z.string().trim().min(2).max(120),
    category: z.enum(EXERCISE_CATEGORIES),
    difficulty: z.enum(EXERCISE_DIFFICULTIES),
    primaryMuscles: z.array(z.enum(MUSCLE_GROUPS)).min(1),
    secondaryMuscles: z.array(z.enum(MUSCLE_GROUPS)),
    equipment: z.array(z.enum(EQUIPMENT)).min(1),
    contraindications: z.array(z.enum(INJURY_AREAS)),
    howToSteps: z.array(z.string().trim().min(1).max(280)),
    isWeighted: z.boolean(),
    /**
     * Nullable, so a curator can *unset* a wrong guess rather than only
     * replace it with another one. Sending null is "nobody has said", which is
     * a truthful answer and the one the import left behind.
     */
    mechanic: z.enum(MECHANICS).nullable(),
    movementPattern: z.enum(MOVEMENT_PATTERNS).nullable(),
    laterality: z.enum(LATERALITIES).nullable(),
    impact: z.enum(IMPACTS).nullable(),
    axialLoad: z.boolean().nullable(),
    bodyPosition: z.enum(BODY_POSITIONS).nullable(),
    trunkFlexion: z.boolean().nullable(),
    avoidInPregnancy: z.boolean().nullable(),
    requiresPartner: z.boolean().nullable(),
    sortOrder: z.number().int().min(0).max(10_000),
})
    .partial()
    .refine((patch) => Object.keys(patch).length > 0, {
    message: 'Name at least one field to change.',
});
/** The editable fields, in the order the form lays them out. */
export const EXERCISE_EDITABLE_FIELDS = [
    'name',
    'category',
    'difficulty',
    'primaryMuscles',
    'secondaryMuscles',
    'equipment',
    'contraindications',
    'howToSteps',
    'isWeighted',
    'mechanic',
    'movementPattern',
    'laterality',
    'impact',
    'axialLoad',
    'bodyPosition',
    'trunkFlexion',
    'avoidInPregnancy',
    'requiresPartner',
    'sortOrder',
];
export const adminExerciseDeactivateRequestSchema = z.object({
    reason: z.string().trim().min(3).max(280),
});
/**
 * Removing one still.
 *
 * The key travels in the body rather than the path: object keys contain
 * slashes, and a path parameter would have to be encoded at one end and decoded
 * at the other for no gain.
 */
export const adminExerciseImageRequestSchema = z.object({
    key: z.string().min(1).max(255),
});
/**
 * The tabs the detail screen offers, in the order it shows them.
 *
 * `overview` first because a reviewer needs to know what the row claims to be
 * before judging its muscle map; `alternatives` follows muscles because
 * judging a curated substitute depends on already knowing what the movement
 * trains, and `media` last because filming is the work that follows the
 * decision rather than informing it. `history` is every change to the row,
 * who made it and why, with Revert.
 */
export const EXERCISE_DETAIL_TABS = ['overview', 'muscles', 'alternatives', 'media', 'history'];
/**
 * What the demo-clip upload endpoint accepts. Enforced server-side, stated here
 * so the picker can match.
 *
 * `image/gif` sits in the video list rather than the stills list because the
 * column decides, not the codec: `demoVideoKey` holds the one clip that shows
 * the movement, `imageKeys` holds the stills beside it. Every clip in the
 * catalog today is a GIF, mirrored by the catalog engine's exercise-animations
 * domain, so a curator replacing a bad one by hand needs to upload the same
 * kind of file the engine writes.
 */
export const EXERCISE_VIDEO_MIME_TYPES = ['video/mp4', 'video/webm', 'image/gif'];
/**
 * What the stills endpoint accepts.
 *
 * The import mirrors jpegs; webp is here because a curator re-shooting a
 * demonstration should not have to convert first, and png because screenshots
 * of a coaching diagram are a real thing operators upload.
 */
export const EXERCISE_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/**
 * How many stills one exercise may carry.
 *
 * The free-exercise-db import brings two per movement. The cap exists so a
 * mis-scripted upload loop cannot grow one row without bound, not because
 * anybody has a use for the eighth image.
 */
export const EXERCISE_IMAGE_LIMIT = 8;
// --- Steado's pick (the pinned YouTube video) ---------------------------------
/** A YouTube video id: eleven characters of letters, digits, `-` and `_`. */
export const YOUTUBE_VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;
/**
 * The links a video id can be pasted in: watch, share (youtu.be), Shorts,
 * embed and live, with or without a scheme or a `www.`/`m.` host.
 */
const YOUTUBE_LINK_PATTERN = /^(?:https?:\/\/)?(?:(?:www|m|music)\.)?(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:[^#]*&)?v=|(?:shorts|embed|live|v)\/)|youtu\.be\/)([A-Za-z0-9_-]{11})(?:[?&#/].*)?$/i;
/**
 * The video id in whatever an operator pasted: a bare id or a YouTube link.
 * Null when it holds none. Idempotent, so the route's validation and the
 * controller's re-parse agree.
 */
export function youtubeVideoIdFrom(text) {
    const trimmed = text.trim();
    if (YOUTUBE_VIDEO_ID_PATTERN.test(trimmed))
        return trimmed;
    return YOUTUBE_LINK_PATTERN.exec(trimmed)?.[1] ?? null;
}
/**
 * Pinning a video. `videoId` takes a pasted link or a bare id and arrives
 * normalised to the id.
 */
export const adminExerciseVideoPinRequestSchema = z.object({
    videoId: z
        .string()
        .trim()
        .max(300)
        .transform((text, context) => {
        const id = youtubeVideoIdFrom(text);
        if (id === null) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Paste a YouTube link or an eleven-character video id.',
            });
            return z.NEVER;
        }
        return id;
    }),
});
/** A YouTube video as YouTube last described it. */
export const adminExerciseVideoSchema = z.object({
    videoId: z.string(),
    title: z.string(),
    channelTitle: z.string(),
    thumbnailUrl: z.string().nullable(),
    durationSeconds: z.number().int().nullable(),
    madeForKids: z.boolean(),
});
/**
 * The video an operator chose for an exercise, shown in the app as "Steado's
 * pick" beside (never mixed into) the search results.
 *
 * `video` is null when the details are not cached: YouTube's developer
 * policies allow its data to be kept for 30 days, so they are refreshed when
 * the exercise is next opened rather than stored with the pin. The id is
 * always known.
 */
export const adminExerciseVideoPinSchema = z.object({
    exerciseId: z.string().uuid(),
    videoId: z.string(),
    video: adminExerciseVideoSchema.nullable(),
});
//# sourceMappingURL=exercises.js.map